import { Decoration } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./cursor";
import type { Tree } from "@lezer/common";

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

const blockquoteLine = Decoration.line({ class: "cm-md-blockquote" });

function hideQuoteMarks(state: EditorState, syntaxNode: any, decorations: DecorationEntry[]): void {
  const c = syntaxNode.cursor();
  if (!c.firstChild()) return;

  do {
    if (c.name === "QuoteMark") {
      let hideEnd = c.to;
      const nextChar = state.doc.sliceString(c.to, c.to + 1);
      if (nextChar === " ") {
        hideEnd = c.to + 1;
      }
      decorations.push({
        from: c.from,
        to: hideEnd,
        decoration: Decoration.replace({}),
      });
    }
    // Recurse into children to find nested QuoteMarks
    hideQuoteMarks(state, c.node, decorations);
  } while (c.nextSibling());
}

/**
 * Build decorations for blockquotes.
 *
 * When the cursor is NOT anywhere inside the blockquote range, the `> `
 * prefix (QuoteMark tokens) are hidden and each line gets a styled
 * `.cm-md-blockquote` line decoration with a copper left border.
 */
export function buildBlockquoteDecorations(
  state: EditorState,
  tree: Tree,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  tree.iterate({
    from: range.from,
    to: range.to,
    enter(node) {
      if (node.name !== "Blockquote") return;

      // Per-block reveal: if cursor is anywhere in the blockquote, show raw
      if (cursor.head >= node.from && cursor.head <= node.to) return;
      if (cursor.anchor >= node.from && cursor.anchor <= node.to) return;

      // Hide QuoteMark tokens
      // Use a recursive helper since QuoteMarks may be nested
      // inside Paragraph or other children of the Blockquote
      hideQuoteMarks(state, node.node, decorations);

      // Apply line decoration to each line of the blockquote
      const firstLine = state.doc.lineAt(node.from).number;
      const lastLine = state.doc.lineAt(node.to).number;

      for (let lineNum = firstLine; lineNum <= lastLine; lineNum++) {
        const line = state.doc.line(lineNum);
        decorations.push({
          from: line.from,
          to: line.from,
          decoration: blockquoteLine,
        });
      }

      return false; // don't descend further at the top level
    },
  });
}
